import React, { useEffect, useState, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Row, Col, Space, Typography, Button, Form, Input, Select, Alert, Spin, Tag, Switch } from 'antd';
import { ArrowLeftOutlined, SafetyOutlined, StopOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { getApplicationUserByIdentityUserId, updateApplicationUser, loadApplicationUserDto, createApplicationUser } from '../../actions/applicationUserActions';
import UserDashboardManagementDrawer from '../../components/crud/UserDashboardManagementDrawer';
import { listDocumentTemplates } from '../../actions/documentTemplateActions';
import SingleEntitySelectMiniature from '../../components/crud/SingleEntitySelectMiniature.component';
import { useNavigate, useParams } from 'react-router-dom';
import { ThemeContext } from '../../index';
import { theme, message } from 'antd';
import TOTPActivateModal from '../../components/auth/TOTPActivateModal';
import TOTPDeactivateModal from '../../components/auth/TOTPDeactivateModal';
import axios from 'axios';

const { Title } = Typography;

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'fr', label: 'Français' },
  // Add more as needed
];
const THEMES = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

// Add select options for applicationUser fields
const TITLE_OPTIONS = [
  { value: 'Dr', label: 'Dr' },
  { value: 'Mr', label: 'Mr' },
  { value: 'Ms', label: 'Ms' },
  { value: 'Mrs', label: 'Mrs' },
  { value: 'Prof', label: 'Prof' },
];
const SPECIALTY_OPTIONS = [
  { value: 'General Practice', label: 'General Practice' },
  { value: 'Cardiology', label: 'Cardiology' },
  { value: 'Dermatology', label: 'Dermatology' },
  { value: 'Pediatrics', label: 'Pediatrics' },
  { value: 'Psychiatry', label: 'Psychiatry' },
];
const ROLE_OPTIONS = [
  { value: 'Doctor', label: 'Doctor' },
  { value: 'Nurse', label: 'Nurse' },
  { value: 'Admin', label: 'Admin' },
  { value: 'Therapist', label: 'Therapist' },
  { value: 'Technician', label: 'Technician' },
];

export default function ApplicationUserProfileScreen() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const { themeConfig, changeLocale, toggleTheme } = useContext(ThemeContext);

  // Redux state with proper destructuring
  const { userInfo } = useSelector(state => state.userLogin);
  const { loading, error, applicationUser } = useSelector(state => state.applicationUserDetails);
  const { loading: loadingUpdate, error: errorUpdate, success: successUpdate } = useSelector(state => state.applicationUserUpdate);
  const { loading: loadingCreate, error: errorCreate, success: successCreate } = useSelector(state => state.applicationUserCreate);
  const { documentTemplates = [] } = useSelector(state => state.documentTemplateList);

  // Local state for profile and preferences
  const [profile, setProfile] = useState({});
  const [preferences, setPreferences] = useState({ 
    language: '', 
    theme: '', 
    defaultTemplateIds: []
  });
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [form] = Form.useForm();
  const [isTOTPEnabled, setIsTOTPEnabled] = useState(false);
  const [showTOTPActivateModal, setShowTOTPActivateModal] = useState(false);
  const [showTOTPDeactivateModal, setShowTOTPDeactivateModal] = useState(false);
  const [checkingTOTPStatus, setCheckingTOTPStatus] = useState(true);

  const [manageDashboardsOpen, setManageDashboardsOpen] = useState(false);

  const applicationUserAttached = !!(applicationUser && applicationUser.id);

  useEffect(() => {
    dispatch(loadApplicationUserDto());
    dispatch(listDocumentTemplates());
    if (userInfo?.userId && userInfo?.token) {
      dispatch(getApplicationUserByIdentityUserId(userInfo.userId));
      setIsCreateMode(false);
      checkTOTPStatus();
    } else {
      setIsCreateMode(true);
      setCheckingTOTPStatus(false); // Don't show loading if not authenticated
    }
  }, [dispatch, userInfo?.userId, userInfo?.token]);


  const checkTOTPStatus = async () => {
    setCheckingTOTPStatus(true);
    try {
      if (!userInfo?.token) {
        console.log('No token available, skipping TOTP status check');
        setCheckingTOTPStatus(false);
        return;
      }
      
      const response = await axios.get('/dotnet_api/v1/auth/totp-status', {
        headers: {
          Authorization: `Bearer ${userInfo.token}`
        }
      });
      setIsTOTPEnabled(response.data.isTOTPEnabled);
    } catch (error) {
      console.error('Error checking TOTP status:', error);
      // If unauthorized, user might not be logged in properly
      if (error.response?.status === 401) {
        console.log('User not authenticated for TOTP status check');
      }
    }
    setCheckingTOTPStatus(false);
  };


  useEffect(() => {
    if (successUpdate) {
      // reset the state using constant
      // display a success message
      message.success(t('ApplicationUser updated successfully'));
      dispatch({ type: 'PRACTITIONER_UPDATE_RESET' });
    }
  }, [successUpdate]);


  useEffect(() => {
    if (applicationUser && applicationUser.id) {
      setProfile({
        title: applicationUser.title,
        firstName: applicationUser.firstName || userInfo?.firstName,
        lastName: applicationUser.lastName || userInfo?.lastName,
        professionalNumber: applicationUser.professionalNumber,
        specialty: applicationUser.specialty,
        email: applicationUser.email || userInfo?.email,
        phoneNumber: applicationUser.phoneNumber || userInfo?.phoneNumber,
        role: applicationUser.role,
        status: applicationUser.status || 'Active',
        userId: applicationUser.userId || userInfo?.userId,
      });
      let prefs = { language: 'en', theme: 'light', defaultTemplateIds: [] };
      if (applicationUser.preferences) {
        try {
          const json = applicationUser.preferences;
          prefs = {
            language: json.language || 'en',
            theme: json.theme || 'light',
            defaultTemplateIds: Array.isArray(json.defaultTemplateIds) ? json.defaultTemplateIds : [],
          };
        } catch {}
      }
      setPreferences(prefs);
      setIsCreateMode(false);
    } else {
      setIsCreateMode(true);
      setProfile(prev => ({
        ...prev,
        status: 'Active', // default to 'Active' string for backend
      }));
    }
  }, [applicationUser, userInfo]);

  // Auto-populate form fields when profile changes (for edit mode)
  useEffect(() => {
    if (applicationUserAttached && profile && Object.keys(profile).length > 0) {
      form.setFieldsValue(profile);
    }
  }, [applicationUserAttached, profile, form]);

  // Replace the cookie effect with localStorage
  useEffect(() => {
    if (preferences.defaultTemplateIds) {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const updatedUserInfo = {
        ...userInfo,
        applicationUserProfile: {
          ...userInfo.applicationUserProfile,
          preferences: {
            ...userInfo.applicationUserProfile?.preferences,
            defaultTemplateIds: preferences.defaultTemplateIds
          }
        }
      };
      localStorage.setItem('userInfo', JSON.stringify(updatedUserInfo));
    }
  }, [preferences.defaultTemplateIds]);

  // Handlers
  const handleProfileChange = (changed, all) => {
    setProfile(prev => ({ ...prev, ...changed }));
  };
  const handlePreferencesChange = (field, value) => {
    setPreferences(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'language' && changeLocale) {
        changeLocale(value);
      }
      if (field === 'theme' && toggleTheme) {
        const isDark = themeConfig.algorithm === theme.darkAlgorithm;
        if ((value === 'dark' && !isDark) || (value === 'light' && isDark)) {
          toggleTheme();
        }
      }
      return updated;
    });
  };
  const handleSaveProfile = (values) => {
    const updatedData = {
      ...values,
      preferences: {
        ...preferences,
        defaultTemplateIds: preferences.defaultTemplateIds || []
      },
      email: userInfo?.email,
      identityUserId: userInfo?.userId,
      status: isCreateMode ? 'Active' : values.status
    };

    if (applicationUserAttached) {
      dispatch(updateApplicationUser(applicationUser.id, updatedData))
        .then(() => {
          // Refresh applicationUser data in Redux
          dispatch(getApplicationUserByIdentityUserId(userInfo.userId));
        });
    } else {
      dispatch(createApplicationUser(updatedData))
        .then(() => {
          // Refresh applicationUser data in Redux
          dispatch(getApplicationUserByIdentityUserId(userInfo.userId));
        });
    }
  };

  const handleTOTPActivateSuccess = () => {
    setShowTOTPActivateModal(false);
    setIsTOTPEnabled(true);
    message.success('TOTP enabled successfully!');
  };

  const handleTOTPDeactivateSuccess = () => {
    setShowTOTPDeactivateModal(false);
    setIsTOTPEnabled(false);
    message.success('TOTP disabled successfully!');
  };


  useEffect(() => {
    if (successUpdate || successCreate) {
      // Reset the update/create state
      dispatch({ type: 'PRACTITIONER_UPDATE_RESET' });
      if (successCreate) {
        dispatch({ type: 'PRACTITIONER_CREATE_RESET' });
      }
    }
  }, [successUpdate, successCreate, dispatch]);

  // Helper to show fallback for empty values
  const displayValue = v => v === undefined || v === null || v === '' ? '-' : v;

  // User info section component
  const UserInfoSection = () => (
    <Card title={t('User Information')} bordered={false}>
      <Space direction="vertical" style={{ width: '100%' }}>
        <div><b>{t('User Name')}:</b> {displayValue(userInfo?.username || userInfo?.userName)}</div>
        <div><b>{t('Email')}:</b> {displayValue(userInfo?.email)}</div>
        <div><b>{t('First Name')}:</b> {displayValue(userInfo?.firstName)}</div>
        <div><b>{t('Last Name')}:</b> {displayValue(userInfo?.lastName)}</div>
        <div><b>{t('Phone')}:</b> {displayValue(userInfo?.phoneNumber)}</div>
        <div><b>{t('Organization')}:</b> {displayValue(userInfo?.businessOrganisationName)}</div>
        <div><b>{t('Roles')}:</b> {displayValue((userInfo?.roles || []).join(', '))}</div>
      </Space>
    </Card>
  );


  return (
    <div style={{ padding: 16 }}>
      <Space direction="vertical" style={{ width: '100%', marginBottom: 24 }}>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)()}>
          {t('Return back')}
        </Button>
        <Title level={2}>{t('ApplicationUser Profile')}</Title>
      </Space>

      {/* User Info Section - Always show */}
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={8}>
          <UserInfoSection />
        </Col>
      </Row>

      {/* If no applicationUser attached, show create form */}
      {isCreateMode && (
        <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
          <Col xs={24} lg={12}>
            <Card title={t('Create ApplicationUser Profile')} bordered={false}>
              <Form layout="vertical" onFinish={handleSaveProfile} initialValues={profile} size="large">
                <Form.Item label={t('Title')} name="title" rules={[{ required: true, message: t('The Title field is required') }]}> 
                  <Select options={TITLE_OPTIONS} allowClear size="large" placeholder={t('Select title')} />
                </Form.Item>
                <Form.Item label={t('First Name')} name="firstName" rules={[{ required: true, message: t('First Name is required') }]}> 
                  <Input />
                </Form.Item>
                <Form.Item label={t('Last Name')} name="lastName" rules={[{ required: true, message: t('Last Name is required') }]}> 
                  <Input />
                </Form.Item>
                <Form.Item label={t('Professional Number')} name="professionalNumber" rules={[{ required: true, message: t('Professional Number is required') }]}> 
                  <Input />
                </Form.Item>
                <Form.Item label={t('Specialty')} name="specialty" rules={[{ required: true, message: t('Specialty is required') }]}> 
                  <Select options={SPECIALTY_OPTIONS} allowClear size="large" placeholder={t('Select specialty')} />
                </Form.Item>
                <Form.Item label={t('Role')} name="role" rules={[{ required: true, message: t('Role is required') }]}> 
                  <Select options={ROLE_OPTIONS} allowClear size="large" placeholder={t('Select role')} />
                </Form.Item>
                <Form.Item label={t('Phone Number')} name="phoneNumber" rules={[{ required: true, message: t('The PhoneNumber field is required') }]}> 
                  <Input />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={loadingCreate} size="large">
                    {t('Create')}
                  </Button>
                </Form.Item>
                {[errorCreate].filter(Boolean).map((msg, idx) => (
                  <Alert key={idx} type="error" message={typeof msg === 'string' ? msg.trim() : String(msg)} style={{ marginBottom: 8 }} />
                ))}
                {successCreate && <Alert type="success" message={t('ApplicationUser created successfully')} />}
              </Form>
            </Card>
          </Col>
          {/* Preferences Section on creation */}
          <Col xs={24} lg={12}>
            <Card title={t('Preferences')} bordered={false}>
              <Form layout="vertical" size="large">
                <Form.Item label={t('Language')}>
                  <Select
                    value={displayValue(preferences.language)}
                    onChange={val => handlePreferencesChange('language', val)}
                    options={LANGUAGES}
                    size="large"
                  />
                </Form.Item>
                <Form.Item label={t('Theme')}>
                  <Select
                    value={displayValue(preferences.theme)}
                    onChange={val => handlePreferencesChange('theme', val)}
                    options={THEMES}
                    size="large"
                  />
                </Form.Item>
                <Form.Item label={t('Default Templates')}>
                  <Select
                    mode="multiple"
                    value={preferences.defaultTemplateIds && preferences.defaultTemplateIds.length > 0 ? preferences.defaultTemplateIds : []}
                    onChange={val => handlePreferencesChange('defaultTemplateIds', val)}
                    options={documentTemplates.length > 0 ? documentTemplates.map(template => ({
                      value: template.id,
                      label: template.name
                    })) : []}
                    size="large"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
                </Form>
              </Card>
            </Col>
        </Row>
      )}

      {/* Two-Factor Authentication Section - Always show when user is authenticated */}
      {userInfo?.token && (
        <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
          <Col xs={24} lg={8}>
            <Card title={t('Two-Factor Authentication')} bordered={false}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <div style={{ marginBottom: 16 }}>
                  <p style={{ margin: 0, marginBottom: 12, color: '#666' }}>
                    {t('Enhance your account security by enabling TOTP (Time-based One-Time Password) authentication using an authenticator app like Google Authenticator or Authy.')}
                  </p>
                  
                  {checkingTOTPStatus ? (
                    <div style={{ textAlign: 'center', padding: 20 }}>
                      <Spin />
                      <div style={{ marginTop: 8 }}>{t('Checking TOTP status...')}</div>
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: 16 }}>
                        <b>{t('Current Status')}:</b>
                        {isTOTPEnabled ? (
                          <Tag color="green" icon={<SafetyOutlined />}>{t('TOTP Enabled')}</Tag>
                        ) : (
                          <Tag color="orange">{t('TOTP Disabled')}</Tag>
                        )}
                      </div>
                      
                      <div style={{ marginBottom: 16, fontSize: '14px', color: '#666' }}>
                        {isTOTPEnabled 
                          ? t('Your account is secured with app-based two-factor authentication') 
                          : t('Your account is not protected by two-factor authentication')
                        }
                      </div>
                      
                      {isTOTPEnabled ? (
                        <Button 
                          danger
                          icon={<StopOutlined />}
                          onClick={() => setShowTOTPDeactivateModal(true)}
                          size="default"
                        >
                          {t('Disable TOTP')}
                        </Button>
                      ) : (
                        <Button 
                          type="primary"
                          icon={<SafetyOutlined />}
                          onClick={() => setShowTOTPActivateModal(true)}
                          size="default"
                        >
                          {t('Enable TOTP')}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </Space>
            </Card>
          </Col>
        </Row>
      )}

      {/* If applicationUser attached, show edit form (profile and preferences) */}
      {applicationUserAttached && (
        <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
          {/* Profile Section */}
          <Col xs={24} lg={12}>
            <Card title={t('Edit ApplicationUser Profile')} bordered={false}>
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSaveProfile}
                size="large"
                onValuesChange={handleProfileChange}
                disabled={loading || loadingUpdate}
              >
                <Form.Item label={t('Title')} name="title" rules={[{ required: true, message: t('The Title field is required') }]}> 
                  <Select options={TITLE_OPTIONS} allowClear size="large" placeholder={t('Select title')} disabled={loading || loadingUpdate} />
                </Form.Item>
                <Form.Item label={t('First Name')} name="firstName" rules={[{ required: true, message: t('First Name is required') }]}> 
                  <Input disabled={loading || loadingUpdate} />
                </Form.Item>
                <Form.Item label={t('Last Name')} name="lastName" rules={[{ required: true, message: t('Last Name is required') }]}> 
                  <Input disabled={loading || loadingUpdate} />
                </Form.Item>
                <Form.Item label={t('Professional Number')} name="professionalNumber" rules={[{ required: true, message: t('Professional Number is required') }]}> 
                  <Input disabled={loading || loadingUpdate} />
                </Form.Item>
                <Form.Item label={t('Specialty')} name="specialty" rules={[{ required: true, message: t('Specialty is required') }]}> 
                  <Select options={SPECIALTY_OPTIONS} allowClear size="large" placeholder={t('Select specialty')} disabled={loading || loadingUpdate} />
                </Form.Item>
                <Form.Item label={t('Role')} name="role" rules={[{ required: true, message: t('Role is required') }]}> 
                  <Select options={ROLE_OPTIONS} allowClear size="large" placeholder={t('Select role')} disabled={loading || loadingUpdate} />
                </Form.Item>
                <Form.Item label={t('Status')} name="status" rules={[{ required: true, message: t('Status is required') }]}> 
                  <Input disabled size="large" />
                </Form.Item>
                <Form.Item label={t('Phone Number')} name="phoneNumber" rules={[{ required: true, message: t('The PhoneNumber field is required') }]}> 
                  <Input disabled={loading || loadingUpdate} />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={loadingUpdate} size="large" disabled={loading || loadingUpdate}>
                    {t('Save')}
                  </Button>
                </Form.Item>
                {[error, errorUpdate].filter(Boolean).flatMap(err =>
                  typeof err === 'string'
                    ? err.split(/(?=Role:|Title:|Status:|LastName:|FirstName:|Specialty:|PhoneNumber:|IdentityUserId:|ProfessionalNumber:)/g)
                    : [err]
                ).map((msg, idx) => (
                  <Alert key={idx} type="error" message={typeof msg === 'string' ? msg.trim() : String(msg)} style={{ marginBottom: 8 }} />
                ))}
                {successUpdate && <Alert type="success" message={t('Profile updated successfully')} />}
              </Form>
            </Card>
          </Col>

          {/* Preferences Section */}
          <Col xs={24} lg={12}>
            <Card title={t('Preferences')} bordered={false}>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Spin />
                </div>
              ) : (
                <Form layout="vertical" size="large">
                  <Form.Item label={t('Language')}>
                    <Select
                      value={displayValue(preferences.language)}
                      onChange={val => handlePreferencesChange('language', val)}
                      options={LANGUAGES}
                      size="large"
                    />
                  </Form.Item>
                  <Form.Item label={t('Theme')}>
                    <Select
                      value={displayValue(preferences.theme)}
                      onChange={val => handlePreferencesChange('theme', val)}
                      options={THEMES}
                      size="large"
                    />
                  </Form.Item>
                  <Form.Item label={t('Default Templates')}>
                    <Select
                      mode="multiple"
                      value={preferences.defaultTemplateIds && preferences.defaultTemplateIds.length > 0 ? preferences.defaultTemplateIds : []}
                      onChange={val => handlePreferencesChange('defaultTemplateIds', val)}
                      options={documentTemplates.length > 0 ? documentTemplates.map(template => ({
                        value: template.id,
                        label: template.name
                      })) : []}
                      size="large"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Form>
              )}
            </Card>
          </Col>
        </Row>
      )}
      
      {/* User Dashboards Management */}
      {applicationUserAttached && (
        <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
          <Col xs={24} lg={24}>
            <Button type="primary" onClick={() => setManageDashboardsOpen(true)}>
              {t('Manage Dashboards')}
            </Button>
          </Col>
        </Row>
      )}

      <UserDashboardManagementDrawer
        open={manageDashboardsOpen}
        onClose={() => setManageDashboardsOpen(false)}
        applicationUserId={applicationUser?.id}
      />
      
      {/* TOTP Modals */}
      <TOTPActivateModal
        visible={showTOTPActivateModal}
        onCancel={() => setShowTOTPActivateModal(false)}
        onSuccess={handleTOTPActivateSuccess}
        userEmail={userInfo?.email}
      />
      
      <TOTPDeactivateModal
        visible={showTOTPDeactivateModal}
        onCancel={() => setShowTOTPDeactivateModal(false)}
        onSuccess={handleTOTPDeactivateSuccess}
        userEmail={userInfo?.email}
      />

    </div>
  );
} 